import { TrendingDown, TrendingUp } from 'lucide-react';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { formatCompactMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import { financeReadyState, NotReadyBadge, NotReadyNote } from './FinanceNotReady';

export type PayoutKpiMetric = {
  amount: number | null;
  delta: number | null;
  availability?: FinanceChannelFieldAvailability;
};

type PayoutKpiItem = {
  label: string;
  metric?: PayoutKpiMetric;
};

function moneyText(
  amount: number | null | undefined,
  currency: string,
  availability?: FinanceChannelFieldAvailability,
): string {
  const live = (availability ?? 'AVAILABLE') === 'AVAILABLE' && amount != null;
  return live ? formatCompactMoney(amount, currency, { withCode: false }) : '—';
}

function DeltaBadge({ delta }: { delta: number | null | undefined }) {
  if (delta == null || !Number.isFinite(delta)) {
    return <span className="text-sm font-medium text-black/40">—</span>;
  }
  const rising = delta >= 0;
  const Icon = rising ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium tabular-nums',
        rising ? 'text-[#0194F5]' : 'text-rose-500',
      )}
    >
      {rising ? '+' : ''}
      {delta.toFixed(2)}%
      <Icon className="size-4" aria-hidden strokeWidth={2.5} />
    </span>
  );
}

function PayoutKpiTile({
  label,
  currency,
  metric,
}: {
  label: string;
  currency: string;
  metric?: PayoutKpiMetric;
}) {
  const state = financeReadyState(metric?.availability);
  const live = state === 'live' && metric?.amount != null;

  return (
    <div className="rounded-2xl bg-[#cfd3d8] px-5 py-5 text-black shadow-none">
      <div className="flex items-start justify-between gap-2">
        <p className="text-base font-semibold leading-6">{label}</p>
        {!live ? (
          <NotReadyBadge
            state={state === 'live' ? 'not-ready' : state}
            className="border-black/20 bg-black/5 text-black/60 decoration-black/40"
            reason={
              label === 'Refunds & Chargebacks'
                ? 'Not ready until Phase 4 refund schema is wired.'
                : `${label} uses completed transfer status when available.`
            }
          />
        ) : null}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <p className="text-[28px] font-bold leading-none tracking-tight tabular-nums">
          {moneyText(metric?.amount, currency, metric?.availability)}
        </p>
        <DeltaBadge
          delta={
            (metric?.availability ?? 'AVAILABLE') === 'AVAILABLE' ? (metric?.delta ?? null) : null
          }
        />
      </div>
    </div>
  );
}

/**
 * Figma §11 Payout KPI Summary — light-grey tiles under Gross Transaction Value Overview.
 * Paid / Pending hydrate from transfer status; Refunds stay shell until Phase 4.
 */
export function PayoutKpiSummaryCard({
  currency,
  paid,
  pending,
  refunds,
}: {
  currency: string;
  paid?: PayoutKpiMetric;
  pending?: PayoutKpiMetric;
  refunds?: PayoutKpiMetric;
}) {
  const items: PayoutKpiItem[] = [
    {
      label: 'Paid to Creators',
      metric: paid ?? { amount: null, delta: null, availability: 'COMING_SOON' },
    },
    {
      label: 'Pending Payouts',
      metric: pending ?? { amount: null, delta: null, availability: 'COMING_SOON' },
    },
    {
      label: 'Refunds & Chargebacks',
      metric: refunds ?? { amount: null, delta: null, availability: 'COMING_SOON' },
    },
  ];

  const anyNotReady = items.some(
    (item) => financeReadyState(item.metric?.availability) !== 'live' || item.metric?.amount == null,
  );

  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-3">
        {items.map((item) => (
          <PayoutKpiTile
            key={item.label}
            label={item.label}
            currency={currency}
            {...(item.metric ? { metric: item.metric } : {})}
          />
        ))}
      </div>
      {anyNotReady ? (
        <NotReadyNote>
          Refunds & Chargebacks not ready (Phase 4). Paid / Pending use transfer status when
          available.
        </NotReadyNote>
      ) : null}
    </div>
  );
}
